import { useDraggable, useDroppable } from "@dnd-kit/core";

import { createSchematic, Schematic } from "../Schematic";
import {
  SchematicBuilderContext,
  useSchematicBuilderContext,
} from "../SchematicBuilderContext";
import { boolDataAttr } from "../util/boolDataAttr";
import { preventDrag } from "../util/preventDrag";
import { ChildMarker } from "./util/ChildMarker";
import { ChildRemove } from "./util/ChildRemove";
import { PackageNameInput } from "./util/PackageNameInput";

export const ENUM_DND_TYPE = "enum-entry";

export function EnumBuilder() {
  const schematicBuilderContext = useSchematicBuilderContext();
  const { useSchematic, setSchematic, draggedSchematic, disableDrop } =
    schematicBuilderContext;
  const id = useSchematic((schematic) => schematic.id);
  const fields =
    useSchematic((schematic: Schematic) => schematic.children) ?? [];
  const shouldIgnoreDrop =
    draggedSchematic != null && draggedSchematic.id === fields.at(-1)?.id;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `${id}-append`,
    data: { parentId: id, sortBefore: null, ignored: shouldIgnoreDrop },
    disabled: disableDrop || draggedSchematic?.type !== ENUM_DND_TYPE,
  });

  return (
    <div className="enum-builder builder">
      <PackageNameInput />

      {fields.length === 0 && (
        <div
          className="builder-empty"
          data-over={boolDataAttr(isOver)}
          ref={setDroppableRef}
        >
          No entries.
        </div>
      )}

      {fields.length > 0 && (
        <ul className="builder-children">
          {fields.map((field, i) => (
            <SchematicBuilderContext.Provider
              key={field.id}
              value={{
                ...schematicBuilderContext,
                useSchematic: (selector) =>
                  useSchematic((schematic) => selector(schematic.children![i])),
                setSchematic: (toSet) =>
                  setSchematic((schematic) => ({
                    ...schematic,
                    children: schematic.children!.map((f) =>
                      field === f
                        ? {
                            ...f,
                            ...(typeof toSet === "function" ? toSet(f) : toSet),
                          }
                        : f,
                    ),
                  })),
                removeSchematic: () =>
                  setSchematic((schematic) => ({
                    ...schematic,
                    children: schematic.children!.filter((f) => f !== field),
                  })),
                parentId: id,
                ignoreDrop:
                  draggedSchematic != null &&
                  draggedSchematic.id === fields[i - 1]?.id,
              }}
            >
              <EnumEntryBuilder />
            </SchematicBuilderContext.Provider>
          ))}

          <li
            className="builder-child-droppable"
            data-over={boolDataAttr(isOver && !shouldIgnoreDrop)}
            ref={setDroppableRef}
          />
        </ul>
      )}

      <button
        className="builder-input builder-new-child"
        type="button"
        onClick={() => {
          setSchematic((schematic) => ({
            ...schematic,
            children: [
              ...(schematic.children ?? []),
              createSchematic({ childName: "" }),
            ],
          }));
        }}
        {...preventDrag}
      >
        Add entry
      </button>
    </div>
  );
}

const CHILD_NAME_PLACEHOLDER = "ENTRY_NAME";

export function EnumEntryBuilder() {
  const schematicBuilderContext = useSchematicBuilderContext();
  const {
    schematicKinds,
    useSchematic,
    setSchematic,
    draggedSchematic,
    parentId,
    disableDrop,
    ignoreDrop,
  } = schematicBuilderContext;
  const id = useSchematic((schematic) => schematic.id);
  const childName = useSchematic((schematic) => schematic.childName);
  const kind = useSchematic((schematic) => schematic.kind);
  const Builder = schematicKinds.get(kind)?.builder;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging,
  } = useDraggable({
    id,
    data: {
      type: ENUM_DND_TYPE,
      node: <>{[childName, kind].filter((s) => s).join(": ")}</>,
    },
  });
  const shouldIgnoreDrop = isDragging || ignoreDrop;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id,
    data: { parentId, sortBefore: id, ignored: shouldIgnoreDrop },
    disabled: disableDrop || draggedSchematic?.type !== ENUM_DND_TYPE,
  });

  return (
    <>
      <li
        className="builder-child-droppable"
        data-over={boolDataAttr(isOver && !shouldIgnoreDrop)}
        ref={setDroppableRef}
      />

      <li
        className="builder-child"
        data-dragging={boolDataAttr(isDragging)}
        {...listeners}
        {...attributes}
        ref={setDraggableRef}
        data-collapsible={boolDataAttr(Builder != null)}
      >
        <div className="builder-child-content">
          <ChildMarker />
          <input
            className="builder-input enum-builder-entry-id"
            placeholder={CHILD_NAME_PLACEHOLDER}
            value={childName}
            onChange={(evt) => setSchematic({ childName: evt.target.value })}
            required
            pattern="[a-zA-Z_][a-zA-Z0-9_]*"
            style={{
              width: `${(childName || CHILD_NAME_PLACEHOLDER).length}ch`,
            }}
            {...preventDrag}
          />
          <ChildRemove />
        </div>
      </li>
    </>
  );
}
